import React from 'react';
import CirclePicture from '../../components/ui/CirclePicture';
import { ReactComponent as ArrowDown } from '$/images/arrowDown.svg';
import { hasPermission, hasScope } from '$/utils/hasPermission';
import { rolesPermissionsMatrix } from '$/utils/pathConfig';
import { PermissionedLink } from '../../components/Router';
import isMobile from '$/utils/isMobile';
import { NotificationContext } from '../../components/Notifications/NotificationContext';
import { useMe } from '$/hooks/useMe';
import ToggleOnOff from '../../components/ui/Input/Basics/ToggleOnOff';
import { doesExist } from '$/utils/string';
import { useThemeContext } from '$/components/ui/ThemeContext';
import { css } from '@emotion/react';
import DropdownComponent from '$/components/ui/Input/Basics/DropdownComponent';
import { useImpersonateUser } from './Ops/useImpersonateUser';

const ToggleNotification: React.FC = () => {
  const { desktopNotificationsEnabled, enableDesktopNotifications } =
    React.useContext(NotificationContext);

  return (
    <div key={desktopNotificationsEnabled.toString()}>
      <ToggleOnOff
        onChange={(val: boolean): void => {
          enableDesktopNotifications(val);
        }}
        labelLeft
        checked={desktopNotificationsEnabled}
        label='Desktop notifications'
        className='px-1_25e pb-1/4e pt-1/2e mx-auto text-sm whitespace-no-wrap'
        labelClassName='text-color-input-option'
      />
    </div>
  );
};

const UserMenu: React.FC<{
  onlyMe?: boolean;
  onlyLogout?: boolean;
  noAvatar?: boolean;
  className?: string;
}> = ({ onlyMe, onlyLogout, noAvatar, className }) => {
  const { supportsNotifications } = React.useContext(NotificationContext);
  const me = useMe();
  const etheme = useThemeContext();
  const { logout } = useImpersonateUser();

  const profileEditPath = rolesPermissionsMatrix.profileEdit.path;
  const profilePlanPath = rolesPermissionsMatrix.profilePlan.path;
  const profileRewardsPath = rolesPermissionsMatrix.profileRewards.path;
  const profileTeamPath = rolesPermissionsMatrix.profileTeam.path;
  const profileIntegrationsPath = rolesPermissionsMatrix.profileIntegrations.path;
  const profileScriptsPath = rolesPermissionsMatrix.profileScripts.path;
  const profileDomainsPath = rolesPermissionsMatrix.profileDomains.path;

  const navigationLinks = React.useMemo(() => {
    return [
      {
        id: 'menu-home',
        title: 'Home',
        path: rolesPermissionsMatrix.home.path,
        onMobile: true,
        hideWithPermission: true,
        hasPermission: hasPermission(rolesPermissionsMatrix.home.path, me),
        onlyMe: false,
      },
      {
        id: 'menu-ops',
        title: 'Ops',
        path: rolesPermissionsMatrix.opsHome.path,
        onMobile: true,
        hideWithPermission: true,
        hasPermission: hasPermission(rolesPermissionsMatrix.opsHome.path, me),
        onlyMe: false,
      },
      {
        id: 'menu-my-profile',
        title: 'My profile',
        path: profileEditPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileEditPath, me),
        onlyMe: true,
      },
      {
        id: 'menu-my-plan',
        title: 'My plan',
        path: profilePlanPath,
        onMobile: true,
        hideWithPermission: false,
        hasPermission: hasPermission(profilePlanPath, me),
        onlyMe: false,
      },
      {
        id: 'menu-my-team',
        title: 'My team',
        path: profileTeamPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileTeamPath, me),
        onlyMe: false,
      },
      {
        id: 'menu-my-integrations',
        title: 'Integrations',
        path: profileIntegrationsPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileIntegrationsPath, me),
        onlyMe: false,
      },
      {
        id: 'menu-my-scripts',
        title: 'Scripts manager',
        path: profileScriptsPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileScriptsPath, me),
        onlyMe: false,
      },
      {
        id: 'menu-my-domains',
        title: 'Domains',
        path: profileDomainsPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileDomainsPath, me),
      },
      {
        component: (
          <a key='help' href={'https://ewebinar.com/help'} target='_blank'>
            <DropdownComponent.Options.Item>Help center</DropdownComponent.Options.Item>
          </a>
        ),
      },
      {
        component: (
          <div
            key='support'
            onClick={(): void => {
              if (typeof window !== 'undefined' && window.HubSpotConversations) {
                window.HubSpotConversations.widget.open();
              } else {
                location.href = 'https://ewebinar.com/help';
              }
            }}
          >
            <DropdownComponent.Options.Item>Contact support</DropdownComponent.Options.Item>
          </div>
        ),
      },
      {
        id: 'menu-my-rewards',
        title: 'Earn $100 cash now 🤑',
        className: 'font-bold',
        path: profileRewardsPath,
        onMobile: false,
        hideWithPermission: false,
        hasPermission: hasPermission(profileRewardsPath, me),
        onlyMe: false,
      },
      {
        component: (
          <DropdownComponent.Options.Item
            id={me.impersonatingUser ? 'decloak' : 'logout'}
            onClick={logout}
          >
            {me.impersonatingUser ? 'Decloak' : 'Log out'}
          </DropdownComponent.Options.Item>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  if (!doesExist(me)) {
    return null;
  }

  return (
    <DropdownComponent optionsClass={isMobile ? 'relative' : ''} className={className}>
      <DropdownComponent.Reference>
        <div id='userMenu' className='UserMenu flex items-center'>
          {!noAvatar && (
            <CirclePicture
              size={30}
              className='ProfileImage mr-3'
              pictureRef={me.profileMediaRef}
            />
          )}
          <div
            className='UserName flex items-center'
            css={css`
              color: ${etheme.header.fg};
            `}
          >
            {me.firstName} <ArrowDown className='mx-2' />
          </div>
        </div>
      </DropdownComponent.Reference>
      <DropdownComponent.Options
        modifiers={[
          {
            name: 'computeStyles',
            enabled: false, // set { computedStyle: undefined } will break UI
          },
        ]}
        className={'right-0 absolute'}
      >
        {supportsNotifications && !onlyMe && !onlyLogout && !isMobile && (
          <a
            onClick={(e): void => {
              e.stopPropagation();
            }}
          >
            <ToggleNotification />
          </a>
        )}
        {navigationLinks.map((link, idx) => {
          if (link.component) {
            return <React.Fragment key={idx}>{link.component}</React.Fragment>;
          }

          if (isMobile && !link.onMobile) return null;

          return hasScope(link.path, me) &&
            (link.hasPermission || !link.hideWithPermission) &&
            !onlyLogout &&
            (!onlyMe || link.onlyMe) ? (
            <PermissionedLink key={idx} to={link.path} id={link.id}>
              <DropdownComponent.Options.Item className={link.className}>
                {link.title}
              </DropdownComponent.Options.Item>
            </PermissionedLink>
          ) : null;
        })}
      </DropdownComponent.Options>
    </DropdownComponent>
  );
};

export default UserMenu;
